import { useEffect, useMemo, useState, useCallback } from "react";
import { api, money, formatApiError, STORE_KEY } from "../lib/api";
import { toast } from "sonner";
import {
  PencilSimple, DeviceMobile, Cube, Wrench, Package,
  ArrowUp, ArrowDown, ClockCounterClockwise, WarningCircle,
  ArrowsClockwise, Export, Link as LinkIcon, LinkBreak,
  Plus, ShoppingCart, Check, ArrowsLeftRight, LockKey,
  Storefront, MagnifyingGlass, CaretRight, Stack, Trash, X,
} from "@phosphor-icons/react";
import {
  MANUFACTURERS, DEVICE_TYPES, MODELS, PROBLEMS, orderRepairs,
  arrangeProblems, resolveRepairLayout, REPAIR_GROUPS,
} from "../data/repairCatalog";
import { useAuth } from "../context/AuthContext";
import { userCan } from "../lib/permissions";

const safe = (v) => String(v || "").replace(/[^a-zA-Z0-9_-]/g, "_");

const ADJUST_TYPES = [
  { id: "received", label: "Received New Stock", desc: "A shipment arrived", sign: "+", adminOnly: false },
  { id: "returned", label: "Customer Return", desc: "Item returned to shelf", sign: "+", adminOnly: false },
  { id: "adjustment", label: "Stock Count Correction", desc: "Set the real physical count", sign: "±", adminOnly: true },
  { id: "damaged", label: "Damaged / Defective", desc: "Write off a broken unit", sign: "−", adminOnly: true },
];

const MOVEMENT_TYPES = [
  { id: "received", label: "Received", color: "text-emerald-700", bg: "bg-emerald-50", icon: ArrowDown },
  { id: "sale", label: "Sold", color: "text-blue-700", bg: "bg-blue-50", icon: Export },
  { id: "used_for_repair", label: "Used for Repair", color: "text-orange-700", bg: "bg-orange-50", icon: Wrench },
  { id: "void_restock", label: "Restocked", color: "text-amber-700", bg: "bg-amber-50", icon: ArrowsClockwise },
  { id: "adjustment", label: "Correction", color: "text-purple-700", bg: "bg-purple-50", icon: PencilSimple },
  { id: "damaged", label: "Damaged", color: "text-rose-700", bg: "bg-rose-50", icon: WarningCircle },
  { id: "returned", label: "Returned", color: "text-teal-700", bg: "bg-teal-50", icon: ArrowUp },
  { id: "transfer_out", label: "Sent to Store", color: "text-indigo-700", bg: "bg-indigo-50", icon: ArrowsLeftRight },
  { id: "transfer_in", label: "Received from Store", color: "text-cyan-700", bg: "bg-cyan-50", icon: ArrowsLeftRight },
];

// stock status helper
const stockStatus = (stock, low) => {
  if (stock <= 0) return { key: "out", label: "Out of Stock", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500", num: "text-rose-600" };
  if (stock <= (low || 5)) return { key: "low", label: "Low Stock", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", num: "text-amber-600" };
  return { key: "in", label: "In Stock", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", num: "text-slate-900" };
};

export default function PartsStockTab({ catalog, reloadCatalog, q }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canReceive = userCan(user, "parts.receive");
  const canAdjust = userCan(user, "parts.adjust");
  const canEdit = userCan(user, "parts.edit");
  const canTransfer = userCan(user, "parts.transfer");
  const canDelete = userCan(user, "parts.delete");
  const canManageParts = canAdjust || canEdit || canTransfer || canDelete;
  const currentStoreId = localStorage.getItem(STORE_KEY) || "";

  const [websiteCatalog, setWebsiteCatalog] = useState({ manufacturers: [] });
  const [sharedParts, setSharedParts] = useState([]);
  const [repairStock, setRepairStock] = useState([]);
  const [stores, setStores] = useState([]);
  const [orderMap, setOrderMap] = useState({});
  const [selectedBrand, setSelectedBrand] = useState("apple");
  const [selectedDevice, setSelectedDevice] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  // Modals
  const [adjustEntry, setAdjustEntry] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ type: "received", quantity: "", reason: "" });
  const [adjusting, setAdjusting] = useState(false);
  const [historyEntry, setHistoryEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showCreatePart, setShowCreatePart] = useState(null);
  const [createForm, setCreateForm] = useState({ name: "", sku: "", stock: 0, cost: 0 });
  const [creating, setCreating] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [selectedPartToLink, setSelectedPartToLink] = useState("");
  const [linking, setLinking] = useState(false);
  const [showTransfer, setShowTransfer] = useState(null);
  const [transferForm, setTransferForm] = useState({ dest_store_id: "", quantity: "", note: "" });
  const [transferring, setTransferring] = useState(false);

  const [treeSearch, setTreeSearch] = useState("");
  const [viewMode, setViewMode] = useState("tree");
  const [orderingParts, setOrderingParts] = useState([]);
  const [loadingOrdering, setLoadingOrdering] = useState(false);

  useEffect(() => {
    api.get("/catalog/website").then((r) => setWebsiteCatalog(r.data || { manufacturers: [] })).catch(() => {});
    api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
    api.get("/catalog/website/order/all").then((r) => setOrderMap(r.data || {})).catch(() => {});
    loadSharedParts();
    loadRepairStock();
  }, []);

  const loadSharedParts = useCallback(() => {
    api.get("/repair-parts").then((r) => setSharedParts(r.data || [])).catch(() => {});
  }, []);
  const loadRepairStock = useCallback(() => {
    api.get("/repair-parts/stock/all").then((r) => setRepairStock(r.data || [])).catch(() => {});
  }, []);
  const loadOrdering = useCallback(() => {
    setLoadingOrdering(true);
    api.get("/repair-parts/ordering-stock").then((r) => setOrderingParts(r.data?.items || [])).catch(() => {})
      .finally(() => setLoadingOrdering(false));
  }, []);
  useEffect(() => { if (viewMode === "ordering") loadOrdering(); }, [viewMode, loadOrdering]);

  const reloadAll = async () => { await reloadCatalog(); loadSharedParts(); loadRepairStock(); };

  const otherStores = useMemo(() => stores.filter((s) => s.id !== currentStoreId), [stores, currentStoreId]);

  // Map: any repair ID → shared part
  const repairToSharedPart = useMemo(() => {
    const map = {};
    sharedParts.forEach((sp) => {
      (sp.linked_catalog_ids || []).forEach((eid) => { map[eid] = sp; });
      (sp.linked_website_repair_ids || []).forEach((wid) => { map[wid] = sp; });
    });
    return map;
  }, [sharedParts]);

  const repairStockMap = useMemo(() => {
    const map = {};
    repairStock.forEach((rs) => { map[rs.repair_id] = rs; });
    return map;
  }, [repairStock]);

  // ── Tree builders (unchanged logic) ──
  const brands = useMemo(() => {
    const map = new Map();
    (websiteCatalog.manufacturers || []).forEach((m) => map.set(m.id, { id: m.id, label: m.name, source: "website" }));
    MANUFACTURERS.forEach((m) => { if (!map.has(m.id)) map.set(m.id, { id: m.id, label: m.label, source: "preset" }); });
    (catalog.manufacturer || []).filter((c) => !c.override_of && !c.hidden).forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom" }));
    return [...map.values()].filter((b) => b.id !== "other").sort((a, b) => a.label.localeCompare(b.label));
  }, [websiteCatalog, catalog.manufacturer]);

  const websiteBrand = (brandId) => (websiteCatalog.manufacturers || []).find((m) => m.id === brandId);
  const devicesForBrand = (brandId) => {
    const map = new Map();
    (websiteBrand(brandId)?.device_types || []).forEach((d) => map.set(d.id, { id: d.id, label: d.name, source: "website" }));
    if (!map.size) DEVICE_TYPES.filter((d) => d.id !== "other").forEach((d) => map.set(d.id, { id: d.id, label: d.label, source: "preset" }));
    (catalog.device_type || []).filter((c) => !c.override_of && !c.hidden).filter((c) => c.parent_manufacturer === brandId || (!c.parent_manufacturer && c.parent_device_type === "all")).forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom" }));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  };
  const websiteDevice = (brandId, deviceId) => (websiteBrand(brandId)?.device_types || []).find((d) => d.id === deviceId);
  const modelsForDevice = (brandId, deviceId) => {
    const map = new Map();
    (websiteDevice(brandId, deviceId)?.models || []).forEach((m) => map.set(m.id, { id: m.id, label: m.name, sub: [m.year, m.model_number].filter(Boolean).join(" · "), source: "website", raw: m }));
    (MODELS[`${brandId}|${deviceId}`] || []).forEach((name) => { if (!map.has(name)) map.set(name, { id: name, label: name, source: "preset" }); });
    (catalog.model || []).filter((c) => !c.override_of && !c.hidden).filter((c) => { const mfr = c.parent_manufacturer || ""; const dev = c.parent_device_type || ""; if (!mfr && dev === "all") return true; return mfr === brandId && (dev === deviceId || dev === "all"); }).forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom" }));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  };
  const websiteModel = (brandId, deviceId, modelId) => (websiteDevice(brandId, deviceId)?.models || []).find((m) => m.id === modelId);

  const problemsForScope = (brandId, deviceId, modelId) => {
    const map = new Map();
    const savedOrder = orderMap[`repairs:${brandId}:${deviceId}:${modelId}`] || [];
    const websiteReps = orderRepairs(websiteModel(brandId, deviceId, modelId)?.repairs || [], { savedOrder, idOf: (r) => r.id });
    websiteReps.forEach((r) => {
      const price = Number(r.part_options?.[0]?.price || 0);
      const composite = `${modelId}::${r.id}`;
      const sp = repairToSharedPart[composite];
      const rs = !sp ? repairStockMap[composite] : null;
      const opts = r.part_options || [];
      const options = opts.length > 1 ? opts.map((o) => {
        const key = `${composite}::opt:${o.id}`;
        const ors = repairStockMap[key];
        const osp = repairToSharedPart[key] || null;
        // Once a grade is linked, the shared physical part is its only source
        // of truth. A previous standalone record may still exist from before
        // the link was made, but must never override the shared live quantity.
        return { id: o.id, label: o.label, price: Number(o.price || 0), key, stock: osp ? osp.stock : (ors ? ors.stock : 0), low: osp ? (osp.low_stock_threshold || 5) : (ors ? (ors.low_stock_threshold || 5) : 5), shared_part: osp };
      }) : [];
      map.set(`web_${composite}`, {
        id: `web_${composite}`, label: r.name, price, source: "website",
        rawId: r.id, websiteRepairId: composite, options,
        part_stock: sp ? sp.stock : (rs ? rs.stock : 0),
        part_low_stock: sp ? sp.low_stock_threshold : (rs ? (rs.low_stock_threshold || 5) : 5),
        part_cost: sp ? sp.cost : (rs ? (rs.cost || 0) : 0),
        shared_part: sp || null,
      });
    });
    if (!map.size) PROBLEMS.forEach((p) => map.set(`preset_${p.id}`, { id: `preset_${p.id}`, label: p.label, price: p.price, source: "preset" }));
    (catalog.problem || []).filter((c) => !c.override_of && !c.hidden).filter((c) => isProblemVisible(c, brandId, deviceId, modelId)).forEach((c) => {
      const sp = repairToSharedPart[c.id];
      map.set(`cat_${c.id}`, {
        id: `cat_${c.id}`, rawId: c.id, label: c.label, price: c.price || 0, source: "custom", entry: c,
        part_stock: sp ? sp.stock : (c.part_stock || 0),
        part_low_stock: sp ? sp.low_stock_threshold : (c.part_low_stock || 5),
        part_cost: sp ? sp.cost : (c.part_cost || 0),
        shared_part: sp || null,
        linked_part_id: c.linked_part_id || "",
      });
    });
    // Same arrangement the Repair library and the ticket wizard use, so the
    // drag order and removals set there show up here too.
    return arrangeProblems([...map.values()], {
      ...resolveRepairLayout(orderMap, brandId, deviceId, modelId),
      isCustom: (x) => x.source === "custom",
      groupOf: (x) => REPAIR_GROUPS[x.source] ?? REPAIR_GROUPS.preset,
    });
  };

  const allDevices = useMemo(() => devicesForBrand(selectedBrand), [selectedBrand, catalog, websiteCatalog]);
  const allModels = useMemo(() => modelsForDevice(selectedBrand, selectedDevice), [selectedBrand, selectedDevice, catalog, websiteCatalog]);
  const allProblems = useMemo(() => problemsForScope(selectedBrand, selectedDevice, selectedModel), [selectedBrand, selectedDevice, selectedModel, catalog, websiteCatalog, repairToSharedPart, repairStockMap, orderMap]);

  const brandLabel = (id) => brands.find((b) => b.id === id)?.label || id;
  const deviceLabel = (bId, dId) => devicesForBrand(bId).find((d) => d.id === dId)?.label || dId;
  const modelLabel = (bId, dId, mId) => modelsForDevice(bId, dId).find((m) => m.id === mId)?.label || mId;

  // ── Unified search: brand, device, model, repair name & part SKU ──
  const term = (treeSearch.trim() || String(q || "").trim()).toLowerCase();
  const tokens = useMemo(() => term.split(/\s+/).filter(Boolean), [term]);

  const searchIndex = useMemo(() => {
    if (!tokens.length) return [];
    const rows = [];
    brands.forEach((b) => {
      devicesForBrand(b.id).forEach((d) => {
        modelsForDevice(b.id, d.id).forEach((m) => {
          const bits = [];
          (websiteModel(b.id, d.id, m.id)?.repairs || []).forEach((r) => {
            bits.push(r.name);
            const sp = repairToSharedPart[`${m.id}::${r.id}`];
            if (sp) bits.push(sp.name, sp.sku);
            (r.part_options || []).forEach((o) => {
              bits.push(o.label);
              const osp = repairToSharedPart[`${m.id}::${r.id}::opt:${o.id}`];
              if (osp) bits.push(osp.name, osp.sku);
            });
          });
          rows.push({
            brandId: b.id, deviceId: d.id, modelId: m.id,
            hay: [b.label, d.label, m.label, m.sub, ...bits].filter(Boolean).join(" ").toLowerCase(),
          });
        });
      });
    });
    return rows;
  }, [tokens, brands, catalog, websiteCatalog, repairToSharedPart]);

  const matches = useMemo(() => {
    if (!tokens.length) return null;
    const brandIds = new Set(), deviceIds = new Set(), modelIds = new Set();
    searchIndex.forEach((row) => {
      if (!tokens.every((t) => row.hay.includes(t))) return;
      brandIds.add(row.brandId);
      deviceIds.add(`${row.brandId}|${row.deviceId}`);
      modelIds.add(`${row.brandId}|${row.deviceId}|${row.modelId}`);
    });
    return { brandIds, deviceIds, modelIds, count: modelIds.size };
  }, [tokens, searchIndex]);

  const filteredBrands = matches ? brands.filter((b) => matches.brandIds.has(b.id)) : brands;
  const devices = matches ? allDevices.filter((d) => matches.deviceIds.has(`${selectedBrand}|${d.id}`)) : allDevices;
  const models = matches ? allModels.filter((m) => matches.modelIds.has(`${selectedBrand}|${selectedDevice}|${m.id}`)) : allModels;

  const problems = useMemo(() => {
    if (!tokens.length) return allProblems;
    const hits = allProblems.filter((p) => {
      const hay = [p.label, p.shared_part?.name, p.shared_part?.sku, ...(p.options || []).flatMap((o) => [o.label, o.shared_part?.name, o.shared_part?.sku])]
        .filter(Boolean).join(" ").toLowerCase();
      return tokens.some((t) => t.length > 1 && hay.includes(t));
    });
    return hits.length ? hits : allProblems;
  }, [tokens, allProblems]);

  useEffect(() => {
    if (filteredBrands.length && !filteredBrands.find((b) => b.id === selectedBrand)) {
      setSelectedBrand(filteredBrands[0].id); setSelectedDevice(""); setSelectedModel("");
    }
  }, [filteredBrands, selectedBrand]);
  useEffect(() => { if (devices.length && !devices.find((d) => d.id === selectedDevice)) setSelectedDevice(devices[0]?.id || ""); }, [devices, selectedDevice]);
  useEffect(() => { if (models.length && !models.find((m) => m.id === selectedModel)) setSelectedModel(models[0]?.id || ""); }, [models, selectedModel]);

  // count in-stock repairs for a model (for count pills)
  const modelStockCount = (bId, dId, mId) => {
    const list = problemsForScope(bId, dId, mId);
    return list.filter((p) => (p.part_stock || 0) > 0).length;
  };

  // ── Adjust stock ──
  const openAdjust = (p) => {
    setAdjustEntry(p);
    setAdjustForm({ type: "received", quantity: "", reason: "" });
  };
  const submitAdjust = async () => {
    const at = ADJUST_TYPES.find((t) => t.id === adjustForm.type);
    if (at?.adminOnly && !canAdjust) { toast.error("You don't have permission for stock corrections"); return; }
    if (!at?.adminOnly && !canReceive) { toast.error("You don't have permission to receive stock"); return; }
    let finalQty;
    if (adjustForm.type === "adjustment") {
      const newLevel = parseInt(adjustForm.quantity, 10);
      if (isNaN(newLevel) || newLevel < 0) { toast.error("Enter a valid stock count (0 or more)"); return; }
      const currentStock = adjustEntry.part_stock || 0;
      finalQty = newLevel - currentStock;
      if (finalQty === 0) { toast.info("Stock is already at that level"); return; }
    } else {
      const qty = parseInt(adjustForm.quantity, 10);
      if (!qty || isNaN(qty) || qty < 1) { toast.error("Enter a valid quantity"); return; }
      finalQty = adjustForm.type === "damaged" ? -Math.abs(qty) : qty;
    }
    setAdjusting(true);
    try {
      if (adjustEntry.shared_part) {
        const res = await api.post(`/repair-parts/${adjustEntry.shared_part.id}/stock-adjust`, { type: adjustForm.type, quantity: finalQty, reason: adjustForm.reason });
        toast.success(`Stock updated: ${res.data.stock_before} → ${res.data.stock_after}`);
      } else if (adjustEntry.source === "custom" && adjustEntry.rawId && !adjustEntry.websiteRepairId) {
        const res = await api.post(`/repair-catalog/${adjustEntry.rawId}/stock-adjust`, { type: adjustForm.type, quantity: finalQty, reason: adjustForm.reason });
        toast.success(`Stock updated: ${res.data.stock_before} → ${res.data.stock_after}`);
      } else {
        const repairId = adjustEntry.websiteRepairId || adjustEntry.rawId || "";
        const res = await api.post("/repair-parts/stock/adjust", {
          repair_id: repairId,
          repair_name: `${adjustEntry.label} (${modelLabel(selectedBrand, selectedDevice, selectedModel)})`,
          type: adjustForm.type, quantity: finalQty, reason: adjustForm.reason,
        });
        toast.success(`Stock updated: ${res.data.stock_before} → ${res.data.stock_after}`);
      }
      setAdjustEntry(null);
      await reloadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAdjusting(false); }
  };

  // ── History ──
  const openHistory = async (p) => {
    setHistoryEntry(p); setLoadingHistory(true); setHistory([]);
    try {
      let res;
      // Admins get the full audit trail (movements + ticket usage)
      if (isAdmin) {
        let productId = "";
        if (p.shared_part) productId = `rpart:${p.shared_part.id}`;
        else if (p.websiteRepairId) productId = `wrepair:${p.websiteRepairId}`;
        else if (p.source === "custom" && p.rawId) productId = `wrepair:${p.rawId}`;
        if (productId) {
          res = await api.get(`/repair-parts/full-audit/${encodeURIComponent(productId)}`);
        }
      } else {
        // Staff get basic stock movements only
        if (p.shared_part) res = await api.get(`/repair-parts/${p.shared_part.id}/stock-history`);
        else if (p.source === "custom" && p.rawId && !p.websiteRepairId) res = await api.get(`/repair-catalog/${p.rawId}/stock-history`);
        else if (p.websiteRepairId) res = await api.get(`/repair-parts/stock/${p.websiteRepairId}/history`);
      }
      setHistory(res?.data || []);
    } catch { toast.error("Failed to load history"); }
    finally { setLoadingHistory(false); }
  };

  // ── Create shared part ──
  const openCreatePart = (p) => {
    const modelName = modelLabel(selectedBrand, selectedDevice, selectedModel);
    setShowCreatePart(p);
    setCreateForm({ name: `${p.label} (${modelName})`, sku: "", stock: 0, cost: 0 });
  };
  const submitCreatePart = async () => {
    if (!createForm.name.trim()) { toast.error("Name is required"); return; }
    setCreating(true);
    try {
      const entryId = showCreatePart.rawId || "";
      const webId = showCreatePart.websiteRepairId || "";
      const body = {
        name: createForm.name, sku: createForm.sku,
        stock: Number(createForm.stock) || 0,
        cost: canEdit ? (Number(createForm.cost) || 0) : 0,
        linked_catalog_ids: (showCreatePart.source === "custom" && entryId) ? [entryId] : [],
        linked_website_repair_ids: webId ? [webId] : [],
      };
      const res = await api.post("/repair-parts", body);
      toast.success(`Created shared part: ${res.data.name}`);
      setShowCreatePart(null);
      await reloadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  // ── Link ──
  const openLinkModal = (p) => { setShowLinkModal(p); setSelectedPartToLink(""); };
  const submitLink = async () => {
    if (!selectedPartToLink || !showLinkModal) return;
    setLinking(true);
    try {
      const entryId = showLinkModal.rawId || "";
      const webId = showLinkModal.websiteRepairId || "";
      await api.post(`/repair-parts/${selectedPartToLink}/link`, {
        catalog_entry_ids: (showLinkModal.source === "custom" && entryId) ? [entryId] : [],
        website_repair_ids: webId ? [webId] : [],
      });
      toast.success("Linked to shared part");
      setShowLinkModal(null);
      await reloadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLinking(false); }
  };

  const unlinkPart = async (p) => {
    if (!p.shared_part) return;
    if (!window.confirm(`Unlink "${p.label}" from shared part "${p.shared_part.name}"?`)) return;
    try {
      await api.post(`/repair-parts/${p.shared_part.id}/unlink`, {
        catalog_entry_id: (p.source === "custom" && p.rawId) ? p.rawId : "",
        website_repair_id: p.websiteRepairId || "",
      });
      toast.success("Unlinked");
      await reloadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Transfer ──
  const openTransfer = (p) => {
    setShowTransfer(p);
    setTransferForm({ dest_store_id: otherStores[0]?.id || "", quantity: "", note: "" });
  };
  const transferMeta = (p) => {
    if (!p) return null;
    if (p.shared_part) return { kind: "shared", ref_id: p.shared_part.id };
    if (p.websiteRepairId) return { kind: "simple", ref_id: p.websiteRepairId };
    if (p.source === "custom" && p.rawId) return { kind: "catalog", ref_id: p.rawId };
    return null;
  };
  const submitTransfer = async () => {
    const meta = transferMeta(showTransfer);
    if (!meta) { toast.error("This item can't be transferred"); return; }
    if (!transferForm.dest_store_id) { toast.error("Pick a destination store"); return; }
    const qty = parseInt(transferForm.quantity, 10);
    if (!qty || qty < 1) { toast.error("Enter a valid quantity"); return; }
    if (qty > (showTransfer.part_stock || 0)) { toast.error(`Only ${showTransfer.part_stock || 0} in stock here`); return; }
    setTransferring(true);
    try {
      const res = await api.post("/repair-parts/transfer", {
        kind: meta.kind, ref_id: meta.ref_id,
        repair_name: `${showTransfer.label} (${modelLabel(selectedBrand, selectedDevice, selectedModel)})`,
        dest_store_id: transferForm.dest_store_id, quantity: qty, note: transferForm.note,
      });
      const destName = otherStores.find((s) => s.id === transferForm.dest_store_id)?.name || "store";
      toast.success(`Transferred ${qty} → ${destName}. Here: ${res.data.src_stock} left`);
      setShowTransfer(null);
      await reloadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTransferring(false); }
  };

  // ── Per-option stock (website part options: Incell / Soft / Premium …) ──
  const optionPseudo = (p, o) => ({
    id: o.key, label: `${p.label} — ${o.label}`,
    part_stock: o.stock, part_low_stock: o.low,
    websiteRepairId: o.key, source: "website",
    shared_part: repairToSharedPart[o.key] || null,
  });


  const totalLinked = sharedParts.reduce((a, p) => a + (p.linked_catalog_ids?.length || 0) + (p.linked_website_repair_ids?.length || 0), 0);
  const currentStoreName = stores.find((s) => s.id === currentStoreId)?.name || "";

  return (
    <div className="space-y-4" data-testid="parts-stock-tab">
      {/* Header: role badge + view switcher */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200">
          <button onClick={() => setViewMode("tree")} data-testid="view-tree" className={`h-9 px-4 rounded-lg text-[13px] font-semibold transition-all flex items-center gap-1.5 ${viewMode === "tree" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Cube size={14} weight="bold" />Parts Stock
          </button>
          <button onClick={() => setViewMode("ordering")} data-testid="view-ordering" className={`h-9 px-4 rounded-lg text-[13px] font-semibold transition-all flex items-center gap-1.5 ${viewMode === "ordering" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <ShoppingCart size={14} weight="bold" />Needs Ordering
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${canManageParts ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid="parts-role-badge">
            {canManageParts ? <Check size={12} weight="bold" /> : <LockKey size={12} weight="bold" />}
            {isAdmin ? "Admin — full control" : canManageParts ? "Staff — parts management" : "Staff — view & receive"}
          </span>
        </div>
      </div>

      {viewMode === "ordering" && <OrderingView parts={orderingParts} loading={loadingOrdering} onRefresh={loadOrdering} />}

      {viewMode === "tree" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap bg-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Package size={16} weight="duotone" className="text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900 tracking-tight">Parts Stock Tracker</div>
                {currentStoreName && <div className="text-[11px] text-slate-400 flex items-center gap-1"><Storefront size={11} weight="fill" />{currentStoreName}</div>}
              </div>
            </div>
            <div className="flex-1 min-w-[220px] max-w-[380px] relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={treeSearch}
                onChange={(e) => setTreeSearch(e.target.value)}
                placeholder="Search brand, device, model, repair or SKU…"
                data-testid="parts-tree-search"
                className="w-full h-10 pl-9 pr-20 rounded-xl bg-slate-50 border border-slate-200 text-[13px] focus:border-blue-500 focus:bg-white focus:outline-none"
              />
              {treeSearch && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-[10px] font-mono font-bold text-slate-400" data-testid="parts-tree-search-count">{matches?.count || 0}</span>
                  <button onClick={() => setTreeSearch("")} data-testid="parts-tree-search-clear" className="w-6 h-6 rounded-md bg-slate-200 text-slate-600 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center transition-colors">
                    <X size={11} weight="bold" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium" data-testid="parts-stock-path">
              <span className="text-slate-600 font-semibold">{brandLabel(selectedBrand)}</span>
              <CaretRight size={9} weight="bold" />
              <span className="text-slate-600 font-semibold">{deviceLabel(selectedBrand, selectedDevice)}</span>
              <CaretRight size={9} weight="bold" />
              <span className="text-slate-900 font-bold">{modelLabel(selectedBrand, selectedDevice, selectedModel)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[210px_220px_1fr] min-h-[620px]">
            <Col title="Brands" icon={Wrench} testId="parts-brand-col" tone="subtle">
              {filteredBrands.map((b) => (
                <TreeBtn key={b.id} active={selectedBrand === b.id} onClick={() => { setSelectedBrand(b.id); setSelectedDevice(""); setSelectedModel(""); }} testId={`parts-brand-${safe(b.id)}`}>
                  <span className="truncate">{b.label}</span><SourceDot source={b.source} />
                </TreeBtn>
              ))}
              {!filteredBrands.length && <Empty>No matches</Empty>}
            </Col>

            <Col title="Devices" icon={DeviceMobile} testId="parts-device-col" border>
              {devices.map((d) => (
                <TreeBtn key={d.id} active={selectedDevice === d.id} onClick={() => { setSelectedDevice(d.id); setSelectedModel(""); }} testId={`parts-device-${safe(d.id)}`}>
                  <span className="truncate">{d.label}</span><SourceDot source={d.source} />
                </TreeBtn>
              ))}
              {!devices.length && <Empty>Select a brand</Empty>}
            </Col>

            <div className="min-w-0 grid grid-cols-1 lg:grid-cols-[220px_1fr] border-t xl:border-t-0 xl:border-l border-slate-200">
              <Col title="Models" icon={Cube} testId="parts-model-col" tone="subtle">
                {models.map((m) => {
                  const cnt = modelStockCount(selectedBrand, selectedDevice, m.id);
                  return (
                    <TreeBtn key={m.id} active={selectedModel === m.id} onClick={() => setSelectedModel(m.id)} testId={`parts-model-${safe(m.id)}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{m.label}</div>
                        {m.sub && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{m.sub}</div>}
                      </div>
                      {cnt > 0 && <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full ${selectedModel === m.id ? "bg-blue-500 text-white" : "bg-emerald-100 text-emerald-700"}`}>{cnt}</span>}
                    </TreeBtn>
                  );
                })}
                {!models.length && <Empty>Select a device</Empty>}
              </Col>

              <div className="min-w-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50/40">
                <div className="px-4 py-3 border-b border-slate-200 bg-white">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">Repairs & Parts</div>
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight" data-testid="parts-repair-title">
                    {modelLabel(selectedBrand, selectedDevice, selectedModel)}
                  </h3>
                </div>
                <div className="p-4 space-y-2.5 max-h-[560px] overflow-auto" data-testid="parts-repair-list">
                  {problems.map((p) => (
                    <RepairCard
                      key={p.id} p={p} isAdmin={isAdmin} hasSharedParts={sharedParts.length > 0}
                      canTransfer={otherStores.length > 0}
                      canTransferPerm={canTransfer}
                      canEditPerm={canEdit}
                      onAdjust={() => openAdjust(p)}
                      onHistory={() => openHistory(p)}
                      onCreatePart={() => openCreatePart(p)}
                      onLinkPart={() => openLinkModal(p)}
                      onUnlink={() => unlinkPart(p)}
                      onTransfer={() => openTransfer(p)}
                      onOptAdjust={(o) => openAdjust(optionPseudo(p, o))}
                      onOptHistory={(o) => openHistory(optionPseudo(p, o))}
                      onOptTransfer={(o) => openTransfer(optionPseudo(p, o))}
                      onOptCreatePart={(o) => openCreatePart(optionPseudo(p, o))}
                      onOptLinkPart={(o) => openLinkModal(optionPseudo(p, o))}
                      onOptUnlink={(o) => unlinkPart(optionPseudo(p, o))}
                    />
                  ))}
                  {!problems.length && (
                    <div className="text-center py-14">
                      <Package size={34} weight="duotone" className="mx-auto text-slate-300 mb-2" />
                      <div className="text-sm text-slate-500">No repairs for this model.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Adjust modal ── */}
      {adjustEntry && (
        <Modal onClose={() => setAdjustEntry(null)} testId="parts-adjust-modal">
          <ModalHead over="Stock Adjustment" title={adjustEntry.label}>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-sm text-slate-500">Current stock</span>
              <span className="font-mono font-bold text-lg text-slate-900">{adjustEntry.part_stock || 0}</span>
              {adjustEntry.shared_part && <span className="text-[10px] bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full font-bold">SHARED · {adjustEntry.shared_part.name}</span>}
            </div>
          </ModalHead>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {ADJUST_TYPES.map((t) => {
                const locked = t.adminOnly && !canAdjust;
                const active = adjustForm.type === t.id;
                return (
                  <button key={t.id} type="button" disabled={locked}
                    onClick={() => setAdjustForm({ ...adjustForm, type: t.id })} data-testid={`parts-adjust-type-${t.id}`}
                    className={`text-left p-3 rounded-xl border transition-all ${active ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" : locked ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : "border-slate-200 hover:border-slate-400 bg-white"}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>{t.sign}</span>
                      <span className="text-[13px] font-semibold text-slate-800">{t.label}</span>
                      {locked && <LockKey size={12} weight="fill" className="text-slate-400 ml-auto" />}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">{locked ? "Admin only" : t.desc}</div>
                  </button>
                );
              })}
            </div>
            <Fld label={adjustForm.type === "adjustment" ? "New stock level" : "Quantity"}>
              <input type="number" min={adjustForm.type === "adjustment" ? "0" : "1"} step="1" value={adjustForm.quantity} onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })} placeholder={adjustForm.type === "adjustment" ? "Enter the real count" : "How many units?"} className="w-full h-12 px-3 border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none text-lg font-mono rounded-lg" data-testid="parts-adjust-quantity" autoFocus />
            </Fld>
            {adjustForm.type === "adjustment" && (
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg flex items-center gap-2">
                <WarningCircle size={14} weight="duotone" className="flex-shrink-0" />
                Enter the actual physical count. Current: <strong className="font-mono">{adjustEntry.part_stock || 0}</strong>. We calculate the difference.
              </div>
            )}
            <Fld label={(adjustForm.type === "adjustment" || adjustForm.type === "damaged") ? "Reason (required)" : "Reason (optional)"}>
              <textarea value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} placeholder="e.g. New shipment from supplier…" className="w-full h-20 px-3 py-2 border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none text-sm rounded-lg resize-none" data-testid="parts-adjust-reason" />
            </Fld>
          </div>
          <ModalFooter onCancel={() => setAdjustEntry(null)} onConfirm={submitAdjust} disabled={adjusting || !adjustForm.quantity} loading={adjusting} label="Confirm" testId="parts-adjust-submit" />
        </Modal>
      )}

      {/* ── History / Audit modal ── */}
      {historyEntry && (
        <Modal onClose={() => setHistoryEntry(null)} testId="parts-history-modal" wide>
          <ModalHead over={isAdmin ? "Full Audit Trail" : "Stock Movement History"} title={historyEntry.label}>
            {historyEntry.shared_part && <div className="text-xs text-purple-600 mt-1">Shared part · {historyEntry.shared_part.name}</div>}
            {isAdmin && <div className="text-[10px] text-slate-400 mt-1">Stock movements, transfers, and ticket usage — admin only</div>}
          </ModalHead>
          <div className="flex-1 overflow-y-auto p-5">
            {loadingHistory ? <div className="text-center py-10 text-slate-400">Loading…</div>
              : history.length === 0 ? <div className="text-center py-10"><ClockCounterClockwise size={34} className="mx-auto text-slate-300 mb-2" /><div className="text-sm text-slate-500">No history recorded yet.</div></div>
              : <div className="space-y-2">{history.map((m, i) =>
                  m.event === "ticket_usage"
                    ? <TicketUsageRow key={`tu-${m.ticket_id || i}`} m={m} />
                    : <MovementRow key={m.id || `mv-${i}`} m={m} />
                )}</div>}
          </div>
          <div className="border-t border-slate-200 p-3 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">{history.length} event{history.length !== 1 && "s"}</span>
            <button onClick={() => setHistoryEntry(null)} className="h-10 px-6 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800" data-testid="parts-history-close">Close</button>
          </div>
        </Modal>
      )}

      {/* ── Create shared part modal ── */}
      {showCreatePart && (
        <Modal onClose={() => setShowCreatePart(null)} testId="create-shared-part-modal">
          <ModalHead over="Create Shared Part" title="New shared part">
            <div className="text-xs text-slate-400 mt-1">One part shared across multiple repairs & models. Adjust stock once — every linked repair updates.</div>
          </ModalHead>
          <div className="p-5 space-y-3">
            <Fld label="Part name"><input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. iPhone 12/12 Pro Screen (OLED)" className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none text-sm rounded-lg" data-testid="shared-part-name" autoFocus /></Fld>
            <div className={`grid ${canEdit ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
              <Fld label="SKU"><input value={createForm.sku} onChange={(e) => setCreateForm({ ...createForm, sku: e.target.value })} placeholder="Optional" className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:outline-none text-sm font-mono rounded-lg" /></Fld>
              <Fld label="Stock"><input type="number" min="0" value={createForm.stock} onChange={(e) => setCreateForm({ ...createForm, stock: e.target.value })} className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:outline-none text-sm font-mono rounded-lg" data-testid="shared-part-stock" /></Fld>
              {canEdit && <Fld label="Cost ($)"><input type="number" min="0" step="0.01" value={createForm.cost} onChange={(e) => setCreateForm({ ...createForm, cost: e.target.value })} className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:outline-none text-sm font-mono rounded-lg" /></Fld>}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
              <LinkIcon size={12} weight="bold" className="inline mr-1" />
              Links to: <strong>{showCreatePart.label}</strong>
              <span className="text-blue-500 ml-1">({brandLabel(selectedBrand)} → {deviceLabel(selectedBrand, selectedDevice)} → {modelLabel(selectedBrand, selectedDevice, selectedModel)})</span>
            </div>
          </div>
          <ModalFooter onCancel={() => setShowCreatePart(null)} onConfirm={submitCreatePart} disabled={creating || !createForm.name.trim()} loading={creating} label="Create Part" testId="shared-part-create-submit" color="blue" />
        </Modal>
      )}

      {/* ── Link modal ── */}
      {showLinkModal && (
        <Modal onClose={() => setShowLinkModal(null)} testId="link-part-modal" wide>
          <ModalHead over="Link to Shared Part" title={showLinkModal.label}>
            <div className="text-xs text-slate-400 mt-1">Pick an existing shared part — stock is shared across all linked repairs, even across models & devices.</div>
          </ModalHead>
          <div className="p-6 space-y-2.5 max-h-[60vh] overflow-auto">
            {sharedParts.map((sp) => {
              const linkedCount = (sp.linked_catalog_ids?.length || 0) + (sp.linked_website_repair_ids?.length || 0);
              return (
                <div key={sp.id} className={`flex items-stretch gap-2 rounded-xl border transition-all ${selectedPartToLink === sp.id ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" : "border-slate-200 hover:border-slate-400"}`}>
                  <button onClick={() => setSelectedPartToLink(sp.id)} data-testid={`link-option-${safe(sp.id)}`}
                    className="flex-1 text-left px-4 py-3.5 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-[15px] leading-5 font-semibold text-slate-800 break-words" data-testid={`link-option-name-${safe(sp.id)}`}>{sp.name}</div>
                        <div className="text-[11px] text-slate-400 mt-1">{sp.sku && <span className="font-mono mr-2">{sp.sku}</span>}{linkedCount} repairs linked</div>
                      </div>
                      <div className="shrink-0 min-w-[54px] text-right"><div className="font-mono font-bold text-lg leading-5 text-slate-900" data-testid={`link-option-stock-${safe(sp.id)}`}>{sp.stock}</div><div className="text-[9px] text-slate-400 uppercase mt-1">in stock</div></div>
                    </div>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Permanently delete "${sp.name}"? This removes the shared part and all its links. This cannot be undone.`)) return;
                        try {
                          await api.delete(`/repair-parts/${sp.id}`);
                          toast.success(`Deleted "${sp.name}"`);
                          if (selectedPartToLink === sp.id) setSelectedPartToLink("");
                          setSharedParts((prev) => prev.filter((x) => x.id !== sp.id));
                        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
                      }}
                      data-testid={`delete-shared-part-${safe(sp.id)}`}
                      title="Delete permanently"
                      className="shrink-0 mr-2 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash size={15} weight="bold" />
                    </button>
                  )}
                </div>
              );
            })}
            {!sharedParts.length && <div className="text-center py-4 text-sm text-slate-400">No shared parts yet. Create one first.</div>}
          </div>
          <ModalFooter onCancel={() => setShowLinkModal(null)} onConfirm={submitLink} disabled={linking || !selectedPartToLink} loading={linking} label="Link Part" testId="link-part-submit" color="indigo" />
        </Modal>
      )}

      {/* ── Transfer modal ── */}
      {showTransfer && (
        <Modal onClose={() => setShowTransfer(null)} testId="parts-transfer-modal">
          <ModalHead over="Transfer to Another Store" title={showTransfer.label}>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-sm text-slate-500">Available here</span>
              <span className="font-mono font-bold text-lg text-slate-900">{showTransfer.part_stock || 0}</span>
              {currentStoreName && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Storefront size={11} weight="fill" />{currentStoreName}</span>}
            </div>
          </ModalHead>
          <div className="p-5 space-y-4">
            <Fld label="Send to store">
              <select value={transferForm.dest_store_id} onChange={(e) => setTransferForm({ ...transferForm, dest_store_id: e.target.value })} data-testid="transfer-dest-store" className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:outline-none text-sm rounded-lg bg-white font-medium">
                {!otherStores.length && <option value="">No other stores</option>}
                {otherStores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Fld>
            <Fld label="Quantity to transfer">
              <input type="number" min="1" max={showTransfer.part_stock || 0} value={transferForm.quantity} onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })} placeholder="How many units?" className="w-full h-12 px-3 border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none text-lg font-mono rounded-lg" data-testid="transfer-quantity" autoFocus />
            </Fld>
            <Fld label="Note (optional)">
              <input value={transferForm.note} onChange={(e) => setTransferForm({ ...transferForm, note: e.target.value })} placeholder="e.g. covering a rush job" className="w-full h-11 px-3 border border-slate-200 focus:border-blue-500 focus:outline-none text-sm rounded-lg" data-testid="transfer-note" />
            </Fld>
            <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-2 rounded-lg flex items-center gap-2">
              <ArrowsLeftRight size={14} weight="bold" className="flex-shrink-0" />
              Reduces stock here and adds it to the destination store, with a history log on both sides.
            </div>
          </div>
          <ModalFooter onCancel={() => setShowTransfer(null)} onConfirm={submitTransfer} disabled={transferring || !transferForm.quantity || !transferForm.dest_store_id} loading={transferring} label="Confirm Transfer" testId="transfer-submit" color="indigo" />
        </Modal>
      )}
    </div>
  );
}

// ────── Repair Card ──────
function RepairCard({ p, isAdmin, hasSharedParts, canTransfer, canTransferPerm, canEditPerm, onAdjust, onHistory, onCreatePart, onLinkPart, onUnlink, onTransfer, onOptAdjust, onOptHistory, onOptTransfer, onOptCreatePart, onOptLinkPart, onOptUnlink }) {
  const options = Array.isArray(p.options) ? p.options : [];
  const hasOptions = options.length > 0;
  const optTotal = options.reduce((a, o) => a + (o.stock || 0), 0);
  const stock = hasOptions ? optTotal : (p.part_stock || 0);
  const st = stockStatus(stock, p.part_low_stock);
  const sp = p.shared_part;
  const linkedCount = sp ? ((sp.linked_catalog_ids?.length || 0) + (sp.linked_website_repair_ids?.length || 0)) : 0;
  const tracked = hasOptions || stock > 0 || sp || p.websiteRepairId || (p.source === "custom" && p.rawId);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`part-card-${safe(p.id)}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-slate-900">{p.label}</span>
            <SourceDot source={p.source} />
            {hasOptions && <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"><Stack size={10} weight="bold" />{options.length} GRADES</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {!hasOptions && <span className="text-[11px] text-slate-400">Price <span className="font-mono font-semibold text-slate-600">${money(p.price || 0)}</span></span>}
            {(p.part_cost || 0) > 0 && <span className="text-[11px] text-slate-400">Cost <span className="font-mono font-semibold text-slate-600">${money(p.part_cost)}</span></span>}
          </div>
          {sp && !hasOptions && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                <LinkIcon size={10} weight="bold" />{sp.name}
              </span>
              {linkedCount > 1 && <span className="text-[10px] text-slate-400">shared by {linkedCount} repairs</span>}
              <button onClick={onUnlink} title="Unlink" className="p-0.5 text-slate-400 hover:text-rose-500"><LinkBreak size={12} /></button>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className={`text-3xl font-bold font-mono leading-none ${st.num}`}>{stock}</div>
          <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${st.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{hasOptions ? "total across grades" : (tracked ? st.label : "Not tracked")}
          </span>
        </div>
      </div>

      {/* Per-grade (website part option) stock rows */}
      {hasOptions && (
        <div className="mt-3 space-y-1.5" data-testid={`part-options-${safe(p.id)}`}>
          {options.map((o) => {
            const ost = stockStatus(o.stock || 0, o.low);
            return (
              <div key={o.key} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${o.shared_part ? "border-purple-200 bg-purple-50/40" : "border-indigo-100 bg-indigo-50/40"}`} data-testid={`opt-row-${safe(o.id)}`}>
                <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
                  <Stack size={12} weight="bold" className={`${o.shared_part ? "text-purple-500" : "text-indigo-500"} shrink-0`} />
                  <span className="text-[12px] font-bold text-indigo-900 truncate">{o.label}</span>
                  <span className="text-[10px] font-mono text-slate-400 shrink-0">${money(o.price || 0)}</span>
                  {o.shared_part && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 shrink-0">
                      <LinkIcon size={9} weight="bold" />{o.shared_part.name}
                    </span>
                  )}
                </span>
                <span className={`font-mono font-bold text-sm ${ost.num}`}>{o.stock}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${ost.dot}`} />
                <div className="flex items-center gap-1">
                  <button onClick={() => onOptAdjust(o)} title="Adjust stock" data-testid={`opt-adjust-${safe(o.id)}`} className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-100"><PencilSimple size={13} weight="bold" /></button>
                  <button onClick={() => onOptHistory(o)} title="History" data-testid={`opt-history-${safe(o.id)}`} className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100"><ClockCounterClockwise size={13} weight="bold" /></button>
                  {canTransfer && (canTransferPerm || isAdmin) && <button onClick={() => onOptTransfer(o)} disabled={(o.stock || 0) <= 0} title="Transfer" data-testid={`opt-transfer-${safe(o.id)}`} className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-100 disabled:opacity-40"><ArrowsLeftRight size={13} weight="bold" /></button>}
                  {o.shared_part
                    ? <button onClick={() => onOptUnlink(o)} title="Unlink shared part" data-testid={`opt-unlink-${safe(o.id)}`} className="p-1.5 rounded-md text-rose-500 hover:bg-rose-100"><LinkBreak size={13} weight="bold" /></button>
                    : <>
                        <button onClick={() => onOptCreatePart(o)} title="New shared part" data-testid={`opt-newpart-${safe(o.id)}`} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Plus size={13} weight="bold" /></button>
                        {hasSharedParts && <button onClick={() => onOptLinkPart(o)} title="Link to shared part" data-testid={`opt-link-${safe(o.id)}`} className="p-1.5 rounded-md text-purple-500 hover:bg-purple-100"><LinkIcon size={13} weight="bold" /></button>}
                      </>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* action toolbar (single-stock repairs only) */}
      {!hasOptions && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100 flex-wrap">
          <ActionBtn onClick={onAdjust} icon={PencilSimple} label="Adjust" testId={`part-adjust-${safe(p.id)}`} tone="emerald" />
          <ActionBtn onClick={onHistory} icon={ClockCounterClockwise} label="History" testId={`part-history-${safe(p.id)}`} tone="blue" />
          {canTransfer && (
            (canTransferPerm || isAdmin)
              ? <ActionBtn onClick={onTransfer} icon={ArrowsLeftRight} label="Transfer" testId={`part-transfer-${safe(p.id)}`} tone="indigo" disabled={stock <= 0} />
              : <ActionBtn icon={LockKey} label="Transfer" tone="locked" title="No permission to transfer" testId={`part-transfer-locked-${safe(p.id)}`} />
          )}
          {!sp && (
            <>
              <ActionBtn onClick={onCreatePart} icon={Plus} label="New Part" testId={`part-create-shared-${safe(p.id)}`} tone="slate" />
              {hasSharedParts && <ActionBtn onClick={onLinkPart} icon={LinkIcon} label="Link" testId={`part-link-existing-${safe(p.id)}`} tone="slate" />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testId, tone = "slate", disabled, title }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-400",
    blue: "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-400",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-400",
    violet: "border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-400",
    slate: "border-slate-200 bg-white text-slate-600 hover:border-slate-400",
    locked: "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed",
  };
  return (
    <button onClick={disabled || tone === "locked" ? undefined : onClick} disabled={disabled} title={title} data-testid={testId}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[12px] font-semibold transition-all ${tones[tone]} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <Icon size={13} weight="bold" />{label}
    </button>
  );
}

// ────── Ordering View ──────
function OrderingView({ parts, loading, onRefresh }) {
  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [repairFilter, setRepairFilter] = useState("");
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [belowFilter, setBelowFilter] = useState("");

  const contexts = useMemo(() => parts.flatMap((p) => (p.contexts || []).map((c) => ({ ...c, partId: p.id }))), [parts]);
  const unique = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const manufacturers = useMemo(() => unique(contexts.map((c) => c.manufacturer)), [contexts]);
  const devices = useMemo(() => unique(contexts.filter((c) => !manufacturerFilter || c.manufacturer === manufacturerFilter).map((c) => c.device)), [contexts, manufacturerFilter]);
  const models = useMemo(() => unique(contexts.filter((c) => (!manufacturerFilter || c.manufacturer === manufacturerFilter) && (!deviceFilter || c.device === deviceFilter)).map((c) => c.model)), [contexts, manufacturerFilter, deviceFilter]);
  const repairs = useMemo(() => {
    const inScope = contexts.filter((c) =>
      (!manufacturerFilter || c.manufacturer === manufacturerFilter)
      && (!deviceFilter || c.device === deviceFilter)
      && (!modelFilter || c.model === modelFilter));
    return [...new Map(inScope.map((c) => {
      const key = [c.repair, c.grade].join("||");
      return [key, { key, label: [c.repair, c.grade].filter(Boolean).join(" — ") }];
    })).values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [contexts, manufacturerFilter, deviceFilter, modelFilter]);

  const contextMatches = useCallback((context) =>
    (!manufacturerFilter || context.manufacturer === manufacturerFilter)
    && (!deviceFilter || context.device === deviceFilter)
    && (!modelFilter || context.model === modelFilter)
    && (!repairFilter || [context.repair, context.grade].join("||") === repairFilter), [manufacturerFilter, deviceFilter, modelFilter, repairFilter]);

  const filtered = useMemo(() => {
    const limit = belowFilter === "" ? null : Number(belowFilter);
    return parts.filter((p) => {
      const stock = Number(p.stock || 0);
      const threshold = Number(p.low_stock_threshold || 5);
      const partContexts = p.contexts || [];
      const matchesScope = !manufacturerFilter && !deviceFilter && !modelFilter && !repairFilter
        ? true : partContexts.some(contextMatches);
      const matchesStock = stockFilter === "all" || (stockFilter === "attention" && stock <= threshold) || (stockFilter === "low" && stock > 0 && stock <= threshold) || (stockFilter === "out" && stock <= 0);
      const matchesNumber = limit === null || stock <= limit;
      const term = search.trim().toLowerCase();
      const contextText = partContexts.map((c) => Object.values(c).join(" ")).join(" ").toLowerCase();
      const matchesSearch = !term || `${p.name || ""} ${p.sku || ""} ${contextText}`.toLowerCase().includes(term);
      return matchesScope && matchesStock && matchesNumber && matchesSearch;
    });
  }, [parts, manufacturerFilter, deviceFilter, modelFilter, repairFilter, stockFilter, belowFilter, search]);

  const outCount = parts.filter((p) => (p.stock || 0) <= 0).length;
  const lowCount = parts.filter((p) => (p.stock || 0) > 0 && (p.stock || 0) <= (p.low_stock_threshold || 5)).length;
  const clearFilters = () => { setManufacturerFilter(""); setDeviceFilter(""); setModelFilter(""); setRepairFilter(""); setStockFilter("all"); setBelowFilter(""); setSearch(""); };

  if (loading) return <div className="text-center py-16 text-slate-400">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ordering-stock-summary">
        <KpiCard label="Tracked parts" value={parts.length} accent="border-l-blue-600" icon={Package} onClick={() => setStockFilter("all")} active={stockFilter === "all"} testId="ordering-stock-all" />
        <KpiCard label="Needs attention" value={outCount + lowCount} accent="border-l-amber-500" icon={WarningCircle} valueColor="text-amber-600" onClick={() => setStockFilter("attention")} active={stockFilter === "attention"} testId="ordering-stock-attention" />
        <KpiCard label="Low stock" value={lowCount} accent="border-l-amber-500" icon={ArrowDown} valueColor="text-amber-600" onClick={() => setStockFilter("low")} active={stockFilter === "low"} testId="ordering-stock-low" />
        <KpiCard label="Out of stock" value={outCount} accent="border-l-rose-500" icon={WarningCircle} valueColor="text-rose-600" onClick={() => setStockFilter("out")} active={stockFilter === "out"} testId="ordering-stock-out" />
      </div>

      <div className="border border-slate-200 bg-white shadow-sm" data-testid="ordering-filters">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div><div className="text-sm font-bold text-slate-900">Stock explorer</div><div className="text-[11px] text-slate-500">Filter all tracked repair parts, grades, and stock levels.</div></div>
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[11px] font-bold text-slate-600" data-testid="ordering-results-count">{filtered.length} shown</span>
        </div>
        <div className="grid grid-cols-1 gap-2.5 p-3 sm:grid-cols-2 xl:grid-cols-6">
          <div className="relative xl:col-span-2">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search repair, grade, part or SKU…" data-testid="order-search" className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-[13px] focus:border-blue-500 focus:bg-white focus:outline-none" />
        </div>
        <select value={manufacturerFilter} onChange={(e) => { setManufacturerFilter(e.target.value); setDeviceFilter(""); setModelFilter(""); setRepairFilter(""); }} data-testid="order-filter-manufacturer" className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none">
          <option value="">All manufacturers</option>
          {manufacturers.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={deviceFilter} onChange={(e) => { setDeviceFilter(e.target.value); setModelFilter(""); setRepairFilter(""); }} data-testid="order-filter-device" className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none">
          <option value="">All devices</option>
          {devices.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={modelFilter} onChange={(e) => { setModelFilter(e.target.value); setRepairFilter(""); }} data-testid="order-filter-model" className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none"><option value="">All models</option>{models.map((name) => <option key={name} value={name}>{name}</option>)}</select>
        <select value={repairFilter} onChange={(e) => setRepairFilter(e.target.value)} data-testid="order-filter-repair" className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none"><option value="">All repairs & grades</option>{repairs.map((repair) => <option key={repair.key} value={repair.key}>{repair.label}</option>)}</select>
        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} data-testid="order-filter-stock-status" className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-[13px] font-semibold text-slate-700 focus:border-blue-500 focus:outline-none"><option value="all">All stock</option><option value="attention">Low + out of stock</option><option value="low">Low stock</option><option value="out">Out of stock</option></select>
        <div className="flex gap-2"><input type="number" min="0" value={belowFilter} onChange={(e) => setBelowFilter(e.target.value)} placeholder="Stock ≤ number" data-testid="order-filter-below-number" className="min-w-0 h-10 flex-1 px-3 rounded-lg bg-white border border-slate-200 text-[13px] focus:border-blue-500 focus:outline-none" /><button onClick={onRefresh} title="Refresh stock" data-testid="refresh-ordering" className="h-10 w-10 rounded-lg bg-blue-600 text-white grid place-items-center hover:bg-blue-700"><ArrowsClockwise size={15} weight="bold" /></button></div>
        </div>
        {(manufacturerFilter || deviceFilter || modelFilter || repairFilter || stockFilter !== "all" || belowFilter || search) && <div className="border-t border-slate-100 px-3 py-2"><button onClick={clearFilters} data-testid="order-clear-filters" className="h-8 px-3 rounded-lg bg-rose-50 text-rose-600 text-[12px] font-semibold hover:bg-rose-100">Clear filters</button></div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto"><table className="w-full min-w-[860px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <th className="px-4 py-3">Part</th><th className="px-4 py-3">Repair / grade</th><th className="px-4 py-3">Device</th><th className="px-4 py-3 text-right">Stock</th><th className="px-4 py-3 text-right">Low at</th><th className="px-4 py-3 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const st = stockStatus(p.stock || 0, p.low_stock_threshold);
              const context = (p.contexts || []).find(contextMatches) || p.contexts?.[0] || {};
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors" data-testid={`order-row-${safe(p.id)}`}>
                  <td className="px-4 py-3.5"><div className="flex items-center gap-2"><div className="font-semibold text-slate-800">{p.name}</div>{p.order_marked_at && <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700" data-testid={`order-queued-${safe(p.id)}`}><ShoppingCart size={10} weight="bold" />Queued</span>}</div>{p.sku && <div className="text-[10px] text-slate-400 font-mono">{p.sku}</div>}</td>
                  <td className="px-4 py-3.5"><div className="font-semibold text-slate-700">{context.repair || "—"}</div>{context.grade && <div className="text-[11px] text-blue-700 font-medium">{context.grade}</div>}</td>
                  <td className="px-4 py-3.5"><div className="text-[12px] font-medium text-slate-700">{[context.manufacturer, context.device].filter(Boolean).join(" · ") || "—"}</div><div className="text-[11px] text-slate-400">{context.model || "No model linked"}</div></td>
                  <td className="px-4 py-3.5 text-right"><span className={`inline-flex items-center gap-1 font-mono font-bold ${st.num}`}>{p.stock || 0}<span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /></span></td>
                  <td className="px-4 py-3.5 text-right font-mono text-slate-400">{p.low_stock_threshold || 5}</td>
                  <td className="px-4 py-3.5 text-right font-mono text-slate-500">${money(p.cost || 0)}</td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan="6" className="p-14 text-center"><Check size={34} weight="duotone" className="mx-auto text-emerald-400 mb-2" /><div className="text-sm text-slate-500">No tracked parts match these filters.</div></td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// ────── Shared UI ──────
function Col({ title, icon: Icon, testId, children, border, tone }) {
  return (
    <section className={`min-w-0 border-t first:border-t-0 xl:border-t-0 ${border ? "xl:border-l" : ""} border-slate-200 ${tone === "subtle" ? "bg-slate-50/50" : "bg-white"}`} data-testid={testId}>
      <div className="px-3 py-2.5 border-b border-slate-200 bg-white/60"><div className="flex items-center gap-2"><Icon size={13} weight="duotone" className="text-blue-600" /><h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</h3></div></div>
      <div className="p-2 space-y-1 max-h-[560px] overflow-auto">{children}</div>
    </section>
  );
}

function TreeBtn({ active, onClick, testId, children }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-full flex items-center justify-between gap-2 text-left rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${active ? "bg-blue-600 text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

function Empty({ children }) { return <div className="px-3 py-8 text-xs text-slate-400 text-center">{children}</div>; }

function Modal({ onClose, testId, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white w-full ${wide ? "max-w-2xl max-h-[85vh] flex flex-col" : "max-w-md"} rounded-2xl overflow-hidden shadow-2xl border border-slate-200`} data-testid={testId}>{children}</div>
    </div>
  );
}

function ModalHead({ over, title, children }) {
  return (
    <div className="border-b border-slate-200 p-5">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">{over}</div>
      <h2 className="font-bold text-xl leading-7 tracking-tight text-slate-900 break-words">{title}</h2>
      {children}
    </div>
  );
}

function ModalFooter({ onCancel, onConfirm, disabled, loading, label, testId, color = "emerald" }) {
  const colors = { emerald: "bg-emerald-600 hover:bg-emerald-700", blue: "bg-blue-600 hover:bg-blue-700", indigo: "bg-indigo-600 hover:bg-indigo-700" };
  return (
    <div className="flex border-t border-slate-200">
      <button type="button" onClick={onCancel} className="flex-1 h-12 text-sm font-semibold text-slate-600 hover:bg-slate-50" disabled={loading}>Cancel</button>
      <button type="button" onClick={onConfirm} disabled={disabled} data-testid={testId} className={`flex-1 h-12 ${colors[color]} text-white text-sm font-semibold disabled:opacity-50`}>{loading ? "Saving…" : label}</button>
    </div>
  );
}

function MovementRow({ m }) {
  const mType = m.type || m.event;
  const mt = MOVEMENT_TYPES.find((t) => t.id === mType) || MOVEMENT_TYPES[0];
  const Icon = mt.icon;
  const performer = m.performed_by_name || m.performed_by || "System";
  const hasTicket = !!m.ticket_number;
  const statusColors = {
    new: "bg-blue-100 text-blue-700", open: "bg-blue-100 text-blue-700",
    "in-progress": "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-700", "ready-for-pickup": "bg-teal-100 text-teal-700",
    "picked-up": "bg-slate-200 text-slate-700", collected: "bg-slate-100 text-slate-600",
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${hasTicket ? "border-blue-200 bg-gradient-to-r from-blue-50/80 to-orange-50/40" : `border-slate-100 ${mt.bg}`}`}>
      <div className={`p-2 rounded-lg bg-white ${mt.color}`}><Icon size={16} weight="bold" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${mt.color}`}>{mt.label}</span>
          <span className={`text-xs font-mono font-bold ${m.quantity_change > 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.quantity_change > 0 ? "+" : ""}{m.quantity_change}</span>
          <span className="text-xs text-slate-400 font-mono">{m.quantity_before} → {m.quantity_after}</span>
          {hasTicket && <span className="text-xs font-mono font-bold text-blue-600">#{m.ticket_number}</span>}
          {m.ticket_status && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${statusColors[m.ticket_status] || "bg-slate-100 text-slate-600"}`}>{(m.ticket_status || "").replace(/-/g, " ")}</span>}
        </div>
        {hasTicket && (
          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5">
            <span className="font-semibold">{m.customer_name || "Walk-in"}</span>
            {m.device && <><span className="text-slate-300">·</span><span className="text-slate-500">{m.device}</span></>}
            {m.estimated_cost != null && <><span className="text-slate-300">·</span><span className="font-mono text-slate-500">${money(m.estimated_cost)}</span></>}
          </div>
        )}
        {!hasTicket && m.reason && <div className="text-xs text-slate-600 mt-0.5">{m.reason}</div>}
        {m.sale_number && <div className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`audit-payment-${m.sale_number}`}>Paid in {m.sale_number}</div>}
        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400"><span>by {performer}</span><span>·</span><span>{new Date(m.created_at).toLocaleString()}</span></div>
      </div>
    </div>
  );
}

function TicketUsageRow({ m }) {
  const statusColors = {
    new: "bg-blue-100 text-blue-700", open: "bg-blue-100 text-blue-700",
    "in-progress": "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-700", "ready-for-pickup": "bg-teal-100 text-teal-700",
    "picked-up": "bg-slate-200 text-slate-700", collected: "bg-slate-100 text-slate-600",
  };
  const statusCls = statusColors[m.status] || "bg-slate-100 text-slate-600";
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-blue-100 bg-blue-50/60" data-testid={`audit-ticket-${m.ticket_number}`}>
      <div className="p-2 rounded-lg bg-white text-blue-600"><Wrench size={16} weight="bold" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-blue-700">Used in Ticket</span>
          <span className="text-xs font-mono font-bold text-blue-600">#{m.ticket_number}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${statusCls}`}>{(m.status || "").replace(/-/g, " ")}</span>
        </div>
        <div className="text-xs text-slate-700 mt-0.5">
          <span className="font-semibold">{m.customer_name || "Walk-in"}</span>
          {m.device && <span className="text-slate-400"> · {m.device}</span>}
        </div>
        {m.sale_number && <div className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`audit-ticket-payment-${m.ticket_number}`}>Paid in {m.sale_number}</div>}
        {m.estimated_cost != null && <div className="text-[11px] text-slate-500 font-mono mt-0.5">Estimate: ${money(m.estimated_cost)}</div>}
        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400"><span>by {m.created_by || "Unknown"}</span><span>·</span><span>{new Date(m.created_at).toLocaleString()}</span></div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, icon: Icon, valueColor = "text-slate-900", onClick, active, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm border-l-4 ${accent} transition-all hover:shadow-md ${active ? "ring-2 ring-blue-500/40" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
        <Icon size={16} weight="duotone" className="text-slate-300" />
      </div>
      <div className={`text-3xl font-bold font-mono mt-1 ${valueColor}`}>{value}</div>
    </button>
  );
}

function Fld({ label, children }) {
  return <label className="block"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</div>{children}</label>;
}

function isProblemVisible(c, brandId, deviceId, modelId) {
  const mfr = c.parent_manufacturer || ""; const dev = c.parent_device_type || ""; const mdl = c.parent_model || "";
  if (!mfr && dev === "all") return true; if (!mfr && dev) return dev === deviceId;
  if (mfr && mfr !== brandId) return false; if (dev && dev !== "all" && dev !== deviceId) return false;
  if (mdl && mdl !== modelId) return false; return !!mfr || dev === "all";
}

function SourceDot({ source }) {
  if (source === "website") return <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" title="Website synced" />;
  if (source === "custom") return <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" title="Custom" />;
  return null;
}
